import { type IOrderListItem } from '@/types/IOrder';
import { type IOrderClient, type IOrderRecipient } from '@/types/IOrder';
import { type ISocketOrderEvent } from '@/types/ISocketOrderEvent';
import { OrderStatusesEnum } from '@/types/OrderStatusesEnum';
import { ProviderEnum, WayToGetEnum } from '@/types/ProvidersEnum';

export type TOrderMockScenario = 'newest' | 'backdated';

/** База для номера, когда таблица пуста и опереться не на что. */
const FALLBACK_BASE_SEQUENCE = 900000;

/** Насколько «состарить» заказ задним числом, мс. */
const BACKDATED_OFFSET_MS = 7 * 24 * 60 * 60 * 1000;

const MOCK_PHONE = '79184827011';

/** Учётка в форме бэкенда `par-5087`: без `id`, `bonus` и адресов. */
const MOCK_CLIENT: IOrderClient = {
  uuid: 'e9220767-e111-40d0-afdc-1fecc3b44b7c',
  phone: MOCK_PHONE,
  email: 'pisarevvlad@gmail.com',
  name: 'Владислав',
  surname: 'Писарев',
  patronymic: 'Дмитриевич',
  roles: ['ROLE_REGULAR_CUSTOMER'],
  email_confirmed: true,
  phone_confirmed: true,
  birthday: '1995-12-16T00:00:00+00:00',
  gender: 'male',
  amount_purchased: 22634,
  amount_purchases: 526892,
  percent_purchased: 5.072463768115942
};

const MOCK_RECIPIENT: IOrderRecipient = {
  id: 1,
  name: 'Владислав Писарев',
  email: 'pisarevvlad@gmail.com',
  phone: MOCK_PHONE,
  orderRecipientAddress: {
    address: 'Краснодар, ул. Красная, 1',
    amount: 350,
    city: 'Краснодар',
    delivery_provider: ProviderEnum.CDEK,
    orderTrackingInfo: null,
    delivery_type: WayToGetEnum.DELIVERY,
    id: '0198acb5-9005-7fcb-8a52-000000000001',
    pvz_id: null,
    saved_delivery_min_day_calendar: 2,
    saved_delivery_max_day_calendar: 4,
    saved_delivery_min_day_workday: 2,
    saved_delivery_max_day_workday: 3
  }
};

/**
 * Подбирает порядковый номер мокового заказа так, чтобы он гарантированно не
 * столкнулся с уже показанными и встал на ожидаемое место: «самый новый» —
 * выше всех, «задним числом» — ниже всех.
 */
const pickMockSequence = (
  scenario: TOrderMockScenario,
  existingSequences: readonly number[]
): number => {
  const known = existingSequences.filter((sequence) => sequence > 0);

  if (known.length === 0) {
    return scenario === 'newest'
      ? FALLBACK_BASE_SEQUENCE + 1
      : FALLBACK_BASE_SEQUENCE - 1;
  }

  return scenario === 'newest'
    ? Math.max(...known) + 1
    : Math.max(Math.min(...known) - 1, 1);
};

/**
 * Заказ в форме события `order_post_persist` бэкенда `par-5087` (PAR-5189):
 * без `id`, без позиций, способа оплаты и комментария клиента. Номер — только
 * в хвосте `formated_id`, как у настоящего заказа.
 */
const buildMockOrder = (
  scenario: TOrderMockScenario,
  existingSequences: readonly number[]
): IOrderListItem => {
  const sequence = pickMockSequence(scenario, existingSequences);
  const createdAt = new Date(
    scenario === 'newest' ? Date.now() : Date.now() - BACKDATED_OFFSET_MS
  ).toISOString();

  return {
    uuid: `mock-order-${sequence}`,
    formated_id: `MOCK-${new Date(createdAt).getFullYear()}-${sequence}`,
    created_at: createdAt,
    updated_at: createdAt,
    status: OrderStatusesEnum.NEW,
    payment_status: 'not_paid',
    orderRecipient: MOCK_RECIPIENT,
    client: MOCK_CLIENT,
    promocode: null,
    delivery_amount: 350,
    delivery_provider: ProviderEnum.CDEK,
    delivery_type: WayToGetEnum.DELIVERY,
    delivery_from_city: 'Краснодар',
    planned_date_of_shipment: createdAt,
    managed_by: null
  };
};

/**
 * Собирает сырой кадр события нового заказа для тестовой кнопки. Возвращает
 * строку в том же виде, в каком её отдаёт поле `data:` Mercure, — чтобы
 * эмуляция шла через реальный разбор, а не в обход него.
 *
 * `existingSequences` — порядковые номера уже показанных заказов
 * (`getOrderSequence`): сценарий `newest` встаёт выше всех, `backdated` — ниже
 * всех. Так проверяется, что заказ занимает естественную позицию, а не
 * выдёргивается наверх.
 *
 * @param scenario - какой сценарий эмулируем
 * @param existingSequences - номера заказов, уже присутствующих в таблице
 * @returns JSON-строка события
 */
export const buildMockOrderEvent = (
  scenario: TOrderMockScenario,
  existingSequences: readonly number[]
): string => {
  const order = buildMockOrder(scenario, existingSequences);
  const event: ISocketOrderEvent = {
    event: 'order_post_persist',
    status: order.status,
    datetime: Math.floor(Date.now() / 1000),
    data: order
  };

  return JSON.stringify(event);
};
